import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid2 as Grid, Card, CardContent, Typography, IconButton, Box, CardActionArea, styled, Paper, MenuItem, Select, Tooltip, Switch } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { routes } from '../../routes';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import IEslTemplate from '../../models/esl/eslTemplate';
import eslTemplateService from '../../services/esl/EslTemplateService';
import AddEslTemplateModal from '../../components/esl/modal/AddEslTemplateModal';
import EditOnly from '../../components/permissions/EditOnly';
import { canEdit } from '../../utils/permissions';
import FormInput from '../../components/input/form/FormInput';
import IAdminPageBuildingDetail from '../../models/adminPage/adminPageBuildingDetail';
import { useTranslation } from 'react-i18next';
import BuildingService from '../../services/building/BuildingService';
import Progress from '../../components/progress/Progress';
import { theme } from '../../styles/themes/mainTheme';
import DeleteEslTemplateModal from '../../components/esl/modal/DeleteEslTemplateModal';
import EslTemplatePreview from '../../components/esl/EslTemplatePreview';

const Toolbar = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1%',
  width: '100%',
}));

const EslTemplatesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['eslTemplate', 'common']);

  const [companyTemplates, setCompanyTemplates] = useState<IEslTemplate[]>([]);
  const [buildingTemplates, setBuildingTemplates] = useState<IEslTemplate[]>([]);
  const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number>(-1);
  const [isAddTemplateModalOpen, setIsAddTemplateModalOpen] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState<string>();

  useEffect(() => {
    getBuildings();
    getTemplates();
  }, []);

  useEffect(() => {
    filterTemplates(companyTemplates);
  }, [selectedBuildingId, companyTemplates]);

  const getBuildings = async () => {
    const buildings = await BuildingService.GetBuildingsDetails();
    if (!buildings || !buildings.length) return;

    setBuildings(buildings);
    setSelectedBuildingId(buildings[0].id)
  }

  const getTemplates = async () => {

    const templates = await eslTemplateService.GetEslTemplates();
    if (!templates) return;

    setCompanyTemplates(templates);
    filterTemplates(templates);
  }

  const filterTemplates = (templates: IEslTemplate[]) => {
    if (selectedBuildingId <= 0) return;

    const filteredTemplates = templates.filter((template) => template.buildings.some((b) => b.buildingId == selectedBuildingId)).sort((a, b) => a.name.localeCompare(b.name));
    setBuildingTemplates(filteredTemplates);
  }

  const onToggleTemplateActiveStatus = async (template: IEslTemplate) => {
    const buildings = template.buildings.map(building =>
      building.buildingId === selectedBuildingId
        ? { ...building, isActive: !building.isActive }
        : building
    );

    const updatedTemplate = { ...template, buildings };
    await eslTemplateService.UpdateEslTemplate(updatedTemplate);
    await getTemplates();
  }

  const onOpenDeleteModal = (templateId: string) => {
    setDeleteTemplateId(templateId);
  };

  const onCloseDeleteModal = () => {
    setDeleteTemplateId('');
  };

  const onOpenAddModal = () => {
    setIsAddTemplateModalOpen(true);
  };

  const onCloseAddModal = () => {
    setIsAddTemplateModalOpen(false);
  };

  return (
    <Box>
      <Toolbar>
        <FormInput
          label={t("common:header.building")}
        >
          <Select
            notched
            fullWidth
            defaultValue={-1}
            value={selectedBuildingId}
            onChange={(event) =>
              setSelectedBuildingId(Number(event.target.value))
            }
          >
            {buildings.map((building) => (
              <MenuItem
                key={`building_${building.id}`}
                value={building.id}
              >
                {building.name}
              </MenuItem>
            ))}
          </Select>
        </FormInput>

        <EditOnly>
          <IconButton onClick={onOpenAddModal}><AddCircleOutlineIcon /></IconButton>
        </EditOnly>
        <AddEslTemplateModal isModalOpen={isAddTemplateModalOpen} onCloseModal={onCloseAddModal} />
      </Toolbar>

      {!buildingTemplates.length ? (
        <Typography variant="h5" component="h2" sx={{ textAlign: 'center', marginTop: '10%' }}>
          {t("eslTemplate:noTemplates")}
        </Typography>
      ) : (
        <Grid container spacing={3} paddingY={2}>
          {buildingTemplates.map((template) => (
            <Grid size={{ xs: 12, sm: 3 }}>
              <Card>
                <CardActionArea sx={{ display: "flex" }} onClick={() => navigate(routes.dataRoute.eslTemplateEditor, { state: { id: template.id } })}>
                  <EslTemplatePreview template={template} />
                </CardActionArea>

                <CardContent sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h5" component="h2" noWrap sx={{ maxWidth: '70%' }}>
                    {template.name}
                  </Typography>

                  <Box>
                    <Tooltip title={t("eslTemplate:activeSwitchTooltip")} placement='top' arrow>
                      <Switch checked={template.buildings.find(templateBuilding => templateBuilding.buildingId === selectedBuildingId)?.isActive} onChange={() => onToggleTemplateActiveStatus(template)} disabled={!canEdit()} size='small' />
                    </Tooltip>

                    <EditOnly>
                      <IconButton onClick={() => onOpenDeleteModal(template.id)} sx={{ color: theme.palette.error.main }}>
                        <DeleteIcon />
                      </IconButton>
                    </EditOnly>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {deleteTemplateId && (
        <DeleteEslTemplateModal
          isModalOpen={!!deleteTemplateId}
          onCloseModal={onCloseDeleteModal}
          templateId={deleteTemplateId}
        />
      )}

    </Box >
  );
};

export default EslTemplatesPage;
